/**
 * Where the party is standing, when it is standing in a generated dungeon.
 *
 * **A diagram, not a floorplan.** `RoomExit` carries only a label and a
 * destination room id — no size, shape, direction or coordinate anywhere in
 * the schema or the generation prompt, which explicitly forbids even a bare
 * compass direction in a door's label. This is the same "zones, not squares"
 * stance this app already takes for combat positioning, for the identical
 * reason: asking an LLM for pixel or geometry facts is the unreliable
 * spatial task this codebase avoids everywhere else. So the room is drawn as
 * a node graph — the current room as a box, its exits as labelled lines to
 * neighbouring rooms — genuinely visual, built entirely from data the game
 * already has, and honest about carrying no architecture at all.
 *
 * **Decorative, not interactive.** The diagram never receives a click
 * handler of its own — `aria-hidden`, mirroring `MapPanel.tsx`'s own SVG
 * lines exactly — and the exit-button list below it remains the only way to
 * actually move. A second, parallel interactive surface for the identical
 * "move to this room" action would need its own keyboard/focus handling for
 * no functional gain; the diagram only ever answers "does this read as a
 * map", not "can I act on it".
 *
 * **Every generated room is written with room-wide audience at generation
 * time**, not revealed room-by-room as the party physically visits — so an
 * exit's destination name is always something everyone at the table can
 * already see in `places`, the same way an overland pin's name is legible
 * once its region is revealed. There is no separate "unexplored" state to
 * track here; showing the name honestly reflects what the data actually
 * withholds (nothing, once a dungeon exists) rather than performing a fog
 * this app has no mechanism for at room granularity. The diagram is scoped
 * to exactly what the panel already knows for this reason too — one hop,
 * never the whole dungeon.
 */

import { useState } from 'react';

import type { Place } from '../map/places.js';

export interface RoomView {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly exits: readonly { readonly label: string; readonly toRoomId: string; readonly toName: string }[];
  /** How dangerous this dungeon is meant to be — absent for a room with none set. */
  readonly dungeonLevel?: number;
}

/**
 * The ids a `move` can reach from the party's current place: its exits when it is a dungeon room, `undefined`
 * when it is not (every place is then a destination). Unlike `currentRoom` this keeps an exit whose destination
 * is not in the viewer's places list — the move is still allowed, the picker simply has nothing to show for it.
 */
export function reachableFrom(
  places: readonly Place[],
  partyLocation: string | undefined,
): ReadonlySet<string> | undefined {
  if (!partyLocation) return undefined;
  const here = places.find((place) => place.id === partyLocation);
  if (!here?.exits || here.exits.length === 0) return undefined;
  return new Set(here.exits.map((exit) => exit.toRoomId));
}

/**
 * The party's current room, or `null` when they are not standing in one.
 *
 * A place counts as a room only when it carries `exits` at all — the same
 * "all or none" reading `waypoint` already takes for whether a place is on
 * the map. An exit naming a room this viewer's `places` list does not (yet)
 * contain is dropped rather than shown with a blank name: a stray or
 * cross-campaign id must not render as an empty button.
 */
export function currentRoom(
  places: readonly Place[],
  partyLocation: string | undefined,
): RoomView | null {
  if (!partyLocation) return null;
  const here = places.find((place) => place.id === partyLocation);
  if (!here?.exits || here.exits.length === 0) return null;

  const byId = new Map(places.map((place) => [place.id, place]));
  // **Two doors to the same room are one place to go** (Maz, 2026-10-21: "Flooded Cistern" appeared twice in the
  // chips and as two nodes — two exits, one room). They are merged: one chip, one node, the doors' labels joined.
  const merged = new Map<string, { label: string; toRoomId: string; toName: string }>();
  for (const exit of here.exits) {
    const target = byId.get(exit.toRoomId);
    if (!target) continue;
    const seen = merged.get(exit.toRoomId);
    merged.set(
      exit.toRoomId,
      seen
        ? { ...seen, label: seen.label === exit.label ? seen.label : `${seen.label}; ${exit.label}` }
        : { label: exit.label, toRoomId: exit.toRoomId, toName: target.name },
    );
  }
  const exits = [...merged.values()];

  return {
    id: here.id,
    name: here.name,
    description: here.description,
    exits,
    ...(here.dungeonLevel !== undefined ? { dungeonLevel: here.dungeonLevel } : {}),
  };
}

/**
 * How far along each line from the room to an exit its label sits, 0 at the room and 1 at the exit.
 *
 * Not the midpoint: every line leaves the same centre, so near it the labels of
 * neighbouring exits land on top of each other ("a crack in the walla low,
 * water-slick tunnel"). Further out they are as far apart as the nodes are.
 */
/** The longest a line of a ROOM NAME runs before it wraps: it is centred on a node near the diagram's edge. */
const NODE_NAME_LINE = 12;

/** The longest a line of an exit's label runs before it wraps. The room and its neighbour are 38 units apart. */
const EXIT_LABEL_LINE = 15;

/** Words packed into lines of at most `max` characters; a word longer than that keeps its own line. */
export function wrapLabel(text: string, max = EXIT_LABEL_LINE): readonly string[] {
  const lines: string[] = [];
  for (const word of text.trim().split(/\s+/).filter(Boolean)) {
    const last = lines[lines.length - 1];
    if (last !== undefined && (last + ' ' + word).length <= max) lines[lines.length - 1] = last + ' ' + word;
    else lines.push(word);
  }
  return lines;
}

export interface ExitLabelPlace {
  readonly x: number;
  readonly y: number;
  readonly anchor: 'start' | 'middle' | 'end';
  readonly lines: readonly string[];
}

/**
 * Where an exit's label goes: BESIDE its line, never on it, running away from the centre. Maz, 2026-10-21: the
 * labels overlapped each other and the room names (at Crypt Hall the left and right exits printed on top of each
 * other). Every label is wrapped and offset to one side of its own line — above it for a line running left or
 * right, to the right of it for a line running up or down — so two exits that share an axis do not share a place.
 * Pure and exported, tested for exact placement like `layoutRoomGraph`.
 */
export function layoutExitLabel(centre: { x: number; y: number }, node: { x: number; y: number }, text: string): ExitLabelPlace {
  const dx = node.x - centre.x;
  const dy = node.y - centre.y;
  const vertical = Math.abs(dx) < Math.abs(dy) * 0.5;
  // A vertical exit's label has the whole side of the diagram to itself, so it runs wider and needs fewer lines.
  const lines = wrapLabel(text, vertical ? VERTICAL_LABEL_LINE : EXIT_LABEL_LINE);
  // Mostly vertical: sit to the right of the line, starting just off it — and FURTHER out along it than the
  // others, because the horizontal exits' labels are raised into the quadrant beside the room and a tall block
  // close to the middle runs into them (measured on a four-way room with long labels, 2026-10-21).
  if (vertical) {
    const t = VERTICAL_LABEL_ALONG;
    return { x: centre.x + t * dx + 2, y: centre.y + t * dy - ((lines.length - 1) * LABEL_LEADING) / 2, anchor: 'start', lines };
  }
  // Otherwise ABOVE the line, centred in the clear stretch between the room's circle and the neighbour's, so a
  // long label cannot run over either circle (it did, at the first attempt: the side labels covered the nodes).
  const length = Math.hypot(dx, dy);
  const clear = (length - CENTRE_RADIUS - NODE_RADIUS) / 2 + CENTRE_RADIUS;
  const px = centre.x + (dx / length) * clear;
  const py = centre.y + (dy / length) * clear;
  const raise = 2 + (lines.length - 1) * LABEL_LEADING;
  return { x: px, y: py - raise, anchor: 'middle', lines };
}

/** How far out a vertical exit's label sits: past the quadrant the horizontal exits' labels use. */
const VERTICAL_LABEL_ALONG = 0.74;

/** A vertical exit's label line length: nothing beside it to collide with. */
const VERTICAL_LABEL_LINE = 22;

/** The two circles' radii in the diagram, so a label can be centred between them. */
const CENTRE_RADIUS = 6;
const NODE_RADIUS = 5;

/** The distance between wrapped lines of one label. */
const LABEL_LEADING = 3.6;

export interface RoomGraphNode {
  readonly x: number;
  readonly y: number;
  readonly label: string;
  /** Absent for the centre node — the current room has nowhere to move to. */
  readonly toRoomId?: string;
  readonly exitLabel?: string;
}

/**
 * The current room at the centre, each exit at an equal angular slice
 * around it — a pure function, tested directly for exact positions rather
 * than only through the rendered markup, the same reasoning `currentRoom`
 * itself is tested under.
 *
 * Percentage space (0–100), the same coordinate convention
 * `MapPanel.tsx`'s own line-drawing already uses. The first exit points
 * straight up (`− π / 2`) purely so the layout is legible reading
 * clockwise from the top, not because "up" means anything here.
 */
export function layoutRoomGraph(room: RoomView): readonly RoomGraphNode[] {
  const centre: RoomGraphNode = { x: 50, y: 50, label: room.name };
  const radius = 38;
  const n = room.exits.length;
  const neighbours = room.exits.map((exit, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    return {
      x: 50 + radius * Math.cos(angle),
      y: 50 + radius * Math.sin(angle),
      label: exit.toName,
      toRoomId: exit.toRoomId,
      exitLabel: exit.label,
    };
  });
  return [centre, ...neighbours];
}

/**
 * The diagram itself. Nothing to draw beyond the single room the heading
 * and description already name when there are no exits — `null`, the same
 * "nothing to add" answer the exit list gives in that state.
 */
function RoomGraph({ room }: { readonly room: RoomView }) {
  if (room.exits.length === 0) return null;
  const nodes = layoutRoomGraph(room);
  const centre = nodes[0];
  const neighbours = nodes.slice(1);
  if (!centre) return null;

  return (
    <svg
      // A margin all round, so a room name below the bottom circle or beside a side one is inside the drawing
      // and not hanging off its edge (it was clipped in a cropped capture, and would be by any clipping ancestor).
      viewBox="-12 -14 124 130"
      aria-hidden="true"
      className="mx-auto mt-2 w-full max-w-md overflow-visible"
    >
      {neighbours.map((n) => (
        <line
          key={`line-${n.toRoomId}`}
          x1={centre.x}
          y1={centre.y}
          x2={n.x}
          y2={n.y}
          className="stroke-neutral-400 dark:stroke-neutral-600"
          strokeWidth={0.5}
        />
      ))}
      {neighbours.map((n) => {
        const place = layoutExitLabel(centre, n, n.exitLabel ?? '');
        return (
          <text
            key={`label-${n.toRoomId}`}
            x={place.x}
            y={place.y}
            textAnchor={place.anchor}
            className="fill-neutral-500 stroke-white dark:fill-neutral-400 dark:stroke-neutral-950"
            paintOrder="stroke"
            strokeWidth={0.9}
            strokeLinejoin="round"
            style={{ fontSize: 3.2 }}
          >
            {place.lines.map((line, i) => (
              <tspan key={i} x={place.x} dy={i === 0 ? 0 : LABEL_LEADING}>
                {line}
              </tspan>
            ))}
          </text>
        );
      })}
      <circle cx={centre.x} cy={centre.y} r={6} className="fill-sky-100 stroke-sky-600 dark:fill-sky-950 dark:stroke-sky-500" strokeWidth={0.6} />
      <text x={centre.x} y={centre.y + 10.5} textAnchor="middle" className="fill-sky-900 stroke-sky-100 dark:fill-sky-200 dark:stroke-sky-950" paintOrder="stroke" strokeWidth={0.9} strokeLinejoin="round" style={{ fontSize: 3.8, fontWeight: 600 }}>
        {centre.label}
      </text>
      {neighbours.map((n) => (
        <g key={`node-${n.toRoomId}`}>
          <circle cx={n.x} cy={n.y} r={5} className="fill-neutral-100 stroke-neutral-400 dark:fill-neutral-900 dark:stroke-neutral-600" strokeWidth={0.5} />
          {(() => {
            const lines = wrapLabel(n.label, NODE_NAME_LINE);
            // Above the circle for a node in the top half (the block grows upward), below it otherwise.
            const y = n.y < centre.y ? n.y - 7.5 - (lines.length - 1) * LABEL_LEADING : n.y + 10;
            return (
              <text x={n.x} y={y} textAnchor="middle" className="fill-neutral-700 stroke-neutral-100 dark:fill-neutral-300 dark:stroke-neutral-900" paintOrder="stroke" strokeWidth={0.9} strokeLinejoin="round" style={{ fontSize: 3.6 }}>
                {lines.map((line, i) => (
                  <tspan key={i} x={n.x} dy={i === 0 ? 0 : LABEL_LEADING}>
                    {line}
                  </tspan>
                ))}
              </text>
            );
          })()}
        </g>
      ))}
    </svg>
  );
}

const ROOM_OPEN_KEY = 'portal.roomPanelOpen';

/** Whether the person last left the room panel open. Browser storage may be absent or throw; closed is the default. */
function rememberedOpen(): boolean {
  try {
    return window.localStorage.getItem(ROOM_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Where you are, as ONE LINE until asked for more (Maz, 2026-10-20: the table is story-first; the transcript
 * gets the room). The line carries the room's name and its ways out as buttons, which is what is acted on; the
 * description and the diagram are one click away. The description is also narrated into the transcript, so
 * nothing is lost by folding it.
 */
export function DungeonRoomPanel({
  room,
  onMove,
  defaultOpen,
}: {
  readonly room: RoomView | null;
  /** Declares a `move` toward this exit's room, the ordinary `move` verb. */
  readonly onMove: (targetId: string) => void;
  /** Forces the starting state (tests); otherwise the last choice, remembered per browser. */
  readonly defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(() => defaultOpen ?? rememberedOpen());
  if (!room) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(ROOM_OPEN_KEY, next ? '1' : '0');
    } catch {
      /* private window or blocked storage: the choice just is not remembered */
    }
  };

  return (
    <section
      aria-label="Where you are"
      className="rounded-md border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="font-semibold">
          {room.name}
          {room.dungeonLevel !== undefined && (
            <span className="ml-2 text-xs font-normal text-neutral-500 dark:text-neutral-400">
              Dungeon level {room.dungeonLevel}
            </span>
          )}
        </h2>
        {room.exits.length > 0 ? (
          <ul className="flex flex-wrap gap-1.5">
            {room.exits.map((exit) => (
              <li key={exit.toRoomId}>
                <button
                  type="button"
                  onClick={() => onMove(exit.toRoomId)}
                  // The line stays one line: the chip names where it leads, and the way there ("a rusted iron
                  // door") is the tooltip and the diagram's own label.
                  title={`${exit.label} — ${exit.toName}`}
                  aria-label={`${exit.label} — ${exit.toName}`}
                  className="rounded-md border border-neutral-300 px-2 py-0.5 text-xs hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-900"
                >
                  → {exit.toName}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">No way out from here.</span>
        )}
        <button
          type="button"
          aria-expanded={open}
          onClick={toggle}
          className="ml-auto text-xs text-neutral-500 underline dark:text-neutral-400"
        >
          {open ? 'Hide' : 'Expand'}
        </button>
      </div>
      {open && (
        <div>
          {room.description && (
            <p className="mt-2 text-neutral-700 dark:text-neutral-300">{room.description}</p>
          )}
          <RoomGraph room={room} />
        </div>
      )}
    </section>
  );
}
